/**
 * Cell-module EDITOR registry (client side) — maps a module `kind` to its
 * lazily-loaded React editor. This is the ONLY file that imports `*.client`
 * editor components, and it does so via `React.lazy` so each editor is a
 * separate chunk that loads only when its module is selected.
 *
 * Editors are added here as each module migrates into
 * `src/modules/<kind>/Editor.client.tsx`. The inspector's `ModuleEditorHost`
 * reads this map; kinds without an entry fall back to the inspector's inline
 * editor until migrated.
 *
 * See docs/superpowers/specs/2026-06-06-componentization-and-module-contract-design.md
 */

import type { ComponentType } from "react";
import { lazy } from "react";
import type { Rule, SourceRow } from "@/lib/mapper/types";
import type {
  AccessibleModelsMap,
  DefaultModels,
} from "@/modules/ai/shared";
import type { FieldInputHandle } from "@/modules/_shared/FieldInput.client";

/**
 * Props every migrated editor receives. This is the editor contract and matches
 * the superset of props `MapperInspector`'s `RuleBody` passes to editors.
 */
export type EditorProps = {
  rule: Rule;
  sourceHeaders: string[];
  outputColumns: string[];
  sampleRow: SourceRow | undefined;
  onChange: (r: Rule) => void;
  registerFocusedAutocomplete: (handle: FieldInputHandle | null) => void;
  defaultModels: DefaultModels;
  accessibleModels: AccessibleModelsMap;
  onRefreshSettings: () => void;
  /**
   * Backend reports `capabilities.localClaude` (runs locally with
   * AI_LOCAL_CLI=true). Gates the "Claude (your subscription, local)" option.
   */
  localClaude?: boolean;
  /** Called after a dictionary save so the mapper page can refresh its dictionaryMap. */
  onDictionariesChanged?: () => void;
};

export type LazyEditor = ComponentType<EditorProps>;

/** kind → lazy editor. Populated as modules migrate. */
export const CELL_EDITORS: Record<string, LazyEditor> = {};

CELL_EDITORS["source"] = lazy(() => import("@/modules/source/Editor.client"));

CELL_EDITORS["static"] = lazy(() => import("@/modules/static/Editor.client"));

CELL_EDITORS["template"] = lazy(() => import("@/modules/template/Editor.client"));

CELL_EDITORS["ai"] = lazy(() => import("@/modules/ai/Editor.client"));

CELL_EDITORS["dictionary"] = lazy(() => import("@/modules/dictionary/Editor.client"));

CELL_EDITORS["expression"] = lazy(() => import("@/modules/expression/Editor.client"));

CELL_EDITORS["find-replace"] = lazy(() => import("@/modules/find-replace/Editor.client"));

CELL_EDITORS["computed"] = lazy(() => import("@/modules/computed/Editor.client"));

CELL_EDITORS["conditional"] = lazy(() => import("@/modules/conditional/Editor.client"));

CELL_EDITORS["price-engine"] = lazy(() => import("@/modules/price-engine/Editor.client"));

CELL_EDITORS["javascript"] = lazy(() => import("@/modules/javascript/Editor.client"));

CELL_EDITORS["dropbox-image"] = lazy(() => import("@/modules/dropbox-image/Editor.client"));

/** Look up the lazy editor for a module kind, if one has been migrated. */
export function getEditor(kind: string): LazyEditor | undefined {
  return CELL_EDITORS[kind];
}
